import { Button, Switch, TagsInput, TextInput } from '@mantine/core';
import Select from '@xver/components/Atoms/Select/Select';
import { IconInfoCircle, IconPlus, IconTrash } from '@tabler/icons-react';
import type {
    ContainerResponse,
    EnvironmentVariable,
    ImagePullPolicy,
    PricingType,
    UpdateContainerRequest,
} from '@xver/apis/v1/kufer/containers/model';
import DialogBox from '@xver/components/Atoms/DialogBox/DialogBox';
import { useActiveProject } from '@xver/hooks/useActiveProject';
import { getErrorCode, getStatusCode } from '@xver/utils/error';
import { useMemo } from 'react';
import { Control, Controller, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ContainerConfigForm, {
    type ContainerConfigFormValue,
} from '../../ContainerEngineDeploymentPage/components/ContainerConfigForm';
import { useRegistryCredentialsQuery } from '../../ContainerEnginePage/hooks/useRegistryCredentialsQuery';
import { useVolumesQuery } from '../hooks/useVolumesQuery';
import { useContainerMutations } from '../hooks/useContainerMutations';

interface SettingsTabPanelProps {
    containerId: string;
    container: ContainerResponse;
}

interface FormValues {
    min_replicas: number;
    max_replicas: number;
    pricing_type: PricingType;

    container_port: number;
    memory: number;
    cpu: number;
    entrypoint_command: string[];
    entrypoint_argument: string[];
    request_timeout: number;
    request_concurrency: number;

    environment_variables: EnvironmentVariable[];

    image_pull_policy: ImagePullPolicy;
    mutable_tags: string[];
    enable_auto_update: boolean;
    registry_credential_id: string;
}

function buildDefaultValues(container: ContainerResponse): FormValues {
    const cfg = container.configuration;
    const src = container.source;
    return {
        min_replicas: container.min_replicas,
        max_replicas: container.max_replicas,
        pricing_type: container.pricing_type,

        container_port: cfg?.container_port ?? 8080,
        memory: cfg?.memory ?? 512,
        cpu: cfg?.cpu ?? 1000,
        entrypoint_command: cfg?.entrypoint_command ?? [],
        entrypoint_argument: cfg?.entrypoint_argument ?? [],
        request_timeout: cfg?.request_timeout ?? 300,
        request_concurrency: cfg?.request_concurrency ?? 50,

        environment_variables: cfg?.environment_variables ?? [],

        image_pull_policy: src?.image_pull_policy ?? 'IfNotPresent',
        mutable_tags: src?.mutable_tags ?? [],
        enable_auto_update: src?.enable_auto_update ?? false,
        registry_credential_id: src?.registry_credential_id ?? '',
    };
}

const ARRAY_FIELDS: ReadonlyArray<keyof FormValues> = [
    'entrypoint_command',
    'entrypoint_argument',
    'mutable_tags',
];

function arraysEqual(a: string[], b: string[]): boolean {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
}

function envVarsEqual(a: EnvironmentVariable[], b: EnvironmentVariable[]): boolean {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        if (a[i].name !== b[i].name || a[i].value !== b[i].value) return false;
    }
    return true;
}

/** Leaves out `registry_credential_id`: send it only when the owner changed it in this form. */
function diffPayload(current: FormValues, original: FormValues): UpdateContainerRequest {
    const payload: UpdateContainerRequest = {};
    (Object.keys(current) as Array<keyof FormValues>).forEach((key) => {
        if (key === 'registry_credential_id') return;
        if (key === 'environment_variables') {
            if (!envVarsEqual(current.environment_variables, original.environment_variables)) {
                payload.environment_variables = current.environment_variables;
            }
            return;
        }
        if (ARRAY_FIELDS.includes(key)) {
            if (!arraysEqual(current[key] as string[], original[key] as string[])) {
                (payload as Record<string, unknown>)[key] = current[key];
            }
            return;
        }
        if (current[key] !== original[key]) {
            (payload as Record<string, unknown>)[key] = current[key];
        }
    });
    return payload;
}

function RegistryCredentialField({
    control,
    error,
    onChange,
}: {
    control: Control<FormValues>;
    error?: string;
    onChange: () => void;
}) {
    const { t } = useTranslation('containerEngineInstance');
    const { data, isPending } = useRegistryCredentialsQuery();
    const options = (data?.credentials ?? []).map((credential) => ({
        value: credential.id,
        label: credential.endpoint
            ? `${credential.name} — ${credential.endpoint}`
            : credential.name,
    }));

    return (
        <Controller
            control={control}
            name="registry_credential_id"
            render={({ field }) => (
                <Select
                    label={t('settings.registryCredential')}
                    description={t('settings.registryCredentialDescription')}
                    placeholder={t('settings.registryCredentialPlaceholder')}
                    data={options}
                    value={field.value || null}
                    onChange={(next) => {
                        if (!next) return;
                        field.onChange(next);
                        onChange();
                    }}
                    onBlur={field.onBlur}
                    allowDeselect={false}
                    disabled={isPending}
                    error={error}
                />
            )}
        />
    );
}

export default function SettingsTabPanel({ containerId, container }: SettingsTabPanelProps) {
    const { t } = useTranslation('containerEngineInstance');
    const { updateMutation } = useContainerMutations(containerId);
    const { project, activeProjectId, isResolved } = useActiveProject();
    const canSetCredential =
        container.source?.type === 'url' &&
        isResolved &&
        (activeProjectId === null || project?.role === 'owner');
    const credentialFailed =
        updateMutation.isError && !!updateMutation.variables?.registry_credential_id;
    const credentialError = !credentialFailed
        ? undefined
        : getErrorCode(updateMutation.error) === 'owner_only_source'
          ? t('projects:errors.owner_only_source')
          : getStatusCode(updateMutation.error) === 404
            ? t('settings.registryCredentialNotFound')
            : undefined;

    // RWO block storage cannot be shared across pods, so any volume pins the container to one.
    const { data: volumesData } = useVolumesQuery(containerId);
    const hasVolumes = (volumesData?.volumes.length ?? 0) > 0;

    const defaults = useMemo(() => buildDefaultValues(container), [container]);

    const {
        control,
        handleSubmit,
        register,
        reset,
        setValue,
        watch,
        formState: { dirtyFields },
    } = useForm<FormValues>({
        defaultValues: defaults,
    });
    const envVars = useFieldArray({ control, name: 'environment_variables' });

    const watched = watch();
    const credentialChanged =
        canSetCredential &&
        !!dirtyFields.registry_credential_id &&
        !!watched.registry_credential_id;
    const diff: UpdateContainerRequest = credentialChanged
        ? {
              ...diffPayload(watched, defaults),
              registry_credential_id: watched.registry_credential_id,
          }
        : diffPayload(watched, defaults);
    const hasChanges = Object.keys(diff).length > 0;

    const configFormValue: ContainerConfigFormValue = {
        container_port: watched.container_port,
        cpu: watched.cpu,
        memory: watched.memory,
        request_timeout: watched.request_timeout,
        request_concurrency: watched.request_concurrency,
        min_replicas: watched.min_replicas,
        max_replicas: watched.max_replicas,
    };

    const handleConfigFormChange = (next: ContainerConfigFormValue) => {
        (Object.keys(next) as Array<keyof ContainerConfigFormValue>).forEach((key) => {
            setValue(key, next[key], { shouldDirty: true });
        });
    };

    const isBusy = container.status === 'building' || container.status === 'deploying';
    const isPending = updateMutation.isPending;
    const submitDisabled = isPending || !hasChanges || isBusy;

    const onSubmit = handleSubmit(() => {
        if (!hasChanges) return;
        updateMutation.mutate(diff, {
            onSuccess: () => reset(watched),
        });
    });

    const configurationAvailable = !!container.configuration;

    return (
        <form onSubmit={onSubmit} className="w-full flex flex-col gap-4 mt-4">
            <DialogBox
                label={t('settings.scalingResourcesTitle', 'Scaling and resources')}
                description={t(
                    'settings.scalingResourcesDescription',
                    'Replicas, CPU, memory, port, and request behaviour. Saving will trigger a new revision.'
                )}
                classNames="p-4"
            >
                <div className="flex flex-col gap-4">
                    <ContainerConfigForm
                        value={configFormValue}
                        onChange={handleConfigFormChange}
                        replicasDisabled={hasVolumes}
                    />
                    {hasVolumes && (
                        <div className="flex flex-row items-start gap-2">
                            <IconInfoCircle size={16} className="shrink-0 mt-0.5 text-fg/50" />
                            <p className="text-sm text-fg/68">
                                {t(
                                    'settings.replicasLockedByVolumes',
                                    'Replica settings are locked while this container has volumes attached. Block storage can only be used by a single instance.'
                                )}
                            </p>
                        </div>
                    )}
                    {!configurationAvailable && (
                        <div className="text-sm text-fg/68">
                            {t(
                                'settings.configurationUnavailable',
                                'Configuration details are not available for this container.'
                            )}
                        </div>
                    )}
                </div>
            </DialogBox>

            <DialogBox
                label={t('settings.entrypointTitle', 'Entrypoint')}
                description={t(
                    'settings.entrypointDescription',
                    "Override the container's entrypoint command and arguments."
                )}
                classNames="p-4"
            >
                {!configurationAvailable ? (
                    <div className="text-sm text-fg/68">
                        {t(
                            'settings.configurationUnavailable',
                            'Configuration details are not available for this container.'
                        )}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Controller
                            control={control}
                            name="entrypoint_command"
                            render={({ field }) => (
                                <TagsInput
                                    label={t('settings.entrypointCommand', 'Command')}
                                    value={field.value}
                                    onChange={field.onChange}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="entrypoint_argument"
                            render={({ field }) => (
                                <TagsInput
                                    label={t('settings.entrypointArgument', 'Arguments')}
                                    value={field.value}
                                    onChange={field.onChange}
                                />
                            )}
                        />
                    </div>
                )}
            </DialogBox>

            <DialogBox
                label={t('settings.envVarsTitle', 'Environment variables')}
                description={t(
                    'settings.envVarsDescription',
                    'Key/value pairs injected into the container.'
                )}
                classNames="p-4"
            >
                {!configurationAvailable ? (
                    <div className="text-sm text-fg/68">
                        {t(
                            'settings.configurationUnavailable',
                            'Configuration details are not available for this container.'
                        )}
                    </div>
                ) : (
                    <div className="flex flex-col gap-3">
                        {envVars.fields.map((f, idx) => (
                            <div key={f.id} className="flex items-end gap-2">
                                <TextInput
                                    className="flex-1"
                                    label={idx === 0 ? t('settings.envVarName', 'Name') : undefined}
                                    {...register(`environment_variables.${idx}.name`)}
                                />
                                <TextInput
                                    className="flex-1"
                                    label={
                                        idx === 0 ? t('settings.envVarValue', 'Value') : undefined
                                    }
                                    {...register(`environment_variables.${idx}.value`)}
                                />
                                <button
                                    type="button"
                                    onClick={() => envVars.remove(idx)}
                                    aria-label={t('settings.envVarRemove', 'Remove')}
                                    className="inline-flex h-9 cursor-pointer items-center justify-center rounded-md border border-line bg-surface-2 px-2 text-fg/68 hover:bg-surface-3 hover:text-fg"
                                >
                                    <IconTrash size={16} />
                                </button>
                            </div>
                        ))}
                        <Button
                            type="button"
                            variant="default"
                            leftSection={<IconPlus size={16} />}
                            onClick={() => envVars.append({ name: '', value: '' })}
                            className="self-start"
                        >
                            {t('settings.envVarAdd', 'Add variable')}
                        </Button>
                    </div>
                )}
            </DialogBox>

            <DialogBox
                label={t('settings.sourceTitle', 'Source behaviour')}
                description={t(
                    'settings.sourceDescription',
                    'How the container reacts to image changes.'
                )}
                classNames="p-4"
            >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Controller
                        control={control}
                        name="image_pull_policy"
                        render={({ field }) => (
                            <Select
                                label={t('settings.imagePullPolicy', 'Image pull policy')}
                                data={['Always', 'IfNotPresent', 'Never']}
                                value={field.value}
                                onChange={(v) => v && field.onChange(v as ImagePullPolicy)}
                                onBlur={field.onBlur}
                                allowDeselect={false}
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="mutable_tags"
                        render={({ field }) => (
                            <TagsInput
                                label={t('settings.mutableTags', 'Mutable image tags')}
                                placeholder={t('settings.mutableTagsPlaceholder', 'latest, main')}
                                value={field.value}
                                onChange={field.onChange}
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="enable_auto_update"
                        render={({ field }) => (
                            <Switch
                                label={t(
                                    'settings.enableAutoUpdate',
                                    'Automatically deploy when image updates'
                                )}
                                checked={field.value}
                                onChange={(e) => field.onChange(e.currentTarget.checked)}
                                onBlur={field.onBlur}
                            />
                        )}
                    />
                    {canSetCredential && (
                        <RegistryCredentialField
                            control={control}
                            error={credentialError}
                            onChange={updateMutation.reset}
                        />
                    )}
                </div>
            </DialogBox>

            <div className="flex items-center justify-end gap-3">
                {isBusy && (
                    <span className="text-sm text-fg/68">
                        {t(
                            'settings.cannotEditWhileBuilding',
                            'Cannot edit while the container is building or deploying.'
                        )}
                    </span>
                )}
                {!isBusy && !hasChanges && (
                    <span className="text-sm text-fg/68">
                        {t('settings.noChanges', 'No changes to save')}
                    </span>
                )}
                <Button type="submit" disabled={submitDisabled} loading={isPending}>
                    {isPending
                        ? t('settings.saving', 'Saving…')
                        : t('settings.save', 'Save changes')}
                </Button>
            </div>
        </form>
    );
}
